import * as React from 'react'

/**
 * batch-67 portal-thema (FO-504) - de motieven van de feestelijke thema's als
 * inline SVG in de STREEP-stijl: groen `#CCFA78`, donker `#1A1A18`, de
 * surface-tinten, en oranje uitsluitend bij Koningsdag (het gouden sterretje
 * van Nieuwjaar is de enige andere afwijking, letterlijk uit de spec). Geen
 * uploads, geen externe assets: samen onder de 10 kB (zie de test).
 *
 * `PortalThemaSoort` is hier een lokale spiegel van `@streep/types`:
 * `@streep/ui` heeft bewust geen dependency op dat package (zie
 * `lib/locatie-picker-options.ts`). De portal borgt met een type-assertie dat
 * de twee unions gelijk blijven.
 */

export type PortalThemaSoort =
  | 'VERJAARDAG'
  | 'SINTERKLAAS'
  | 'KERST'
  | 'NIEUWJAAR'
  | 'PASEN'
  | 'KONINGSDAG'

const GROEN = '#CCFA78'
const DONKER = '#1A1A18'
const LICHT = '#F0F1EC'
const RAND = '#CED0C8'
/** Alleen Koningsdag. */
const ORANJE = '#F97316'
/** Alleen het nieuwjaars-sterretje. */
const GOUD = '#F2C94C'

interface MotiefProps {
  soort: PortalThemaSoort
  className?: string
}

/**
 * Het bannermotief (links in de banner), 64x64 viewBox. Decoratief: het
 * thema staat als tekst in de banner, dus `aria-hidden`.
 */
export function ThemaMotief({ soort, className }: MotiefProps) {
  const basis = {
    viewBox: '0 0 64 64',
    fill: 'none',
    'aria-hidden': true as const,
    focusable: 'false' as const,
    className,
  }
  switch (soort) {
    case 'VERJAARDAG':
      return (
        <svg {...basis}>
          <rect x="12" y="34" width="40" height="20" rx="4" fill={GROEN} stroke={DONKER} strokeWidth="2" />
          <path d="M12 42c4 3 8 3 12 0s8-3 12 0 8 3 12 0" stroke={DONKER} strokeWidth="2" strokeLinecap="round" />
          <g fill={DONKER}>
            <rect x="22" y="22" width="4" height="12" rx="1" />
            <rect x="30" y="19" width="4" height="15" rx="1" />
            <rect x="38" y="22" width="4" height="12" rx="1" />
          </g>
          <g fill={GROEN} stroke={DONKER} strokeWidth="1.5">
            <path d="M24 14c-2 3-2 5 0 6 2-1 2-3 0-6z" />
            <path d="M32 11c-2 3-2 5 0 6 2-1 2-3 0-6z" />
            <path d="M40 14c-2 3-2 5 0 6 2-1 2-3 0-6z" />
          </g>
          <g fill={DONKER}>
            <rect x="8" y="12" width="4" height="4" rx="1" transform="rotate(20 10 14)" />
            <rect x="52" y="8" width="4" height="4" rx="1" transform="rotate(-25 54 10)" />
            <rect x="56" y="26" width="3" height="3" rx="1" />
          </g>
        </svg>
      )
    case 'SINTERKLAAS':
      return (
        <svg {...basis}>
          <path d="M6 44h52l-7 10H13z" fill={DONKER} />
          <rect x="18" y="34" width="28" height="10" fill={LICHT} stroke={DONKER} strokeWidth="2" />
          <rect x="36" y="20" width="8" height="14" fill={DONKER} />
          <circle cx="44" cy="14" r="4" fill={RAND} />
          <circle cx="51" cy="8" r="3" fill={RAND} />
          <path d="M24 34V22l8 4-8 4" fill={GROEN} stroke={DONKER} strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M2 58c4-3 8-3 12 0s8 3 12 0 8-3 12 0 8 3 12 0 8-3 10-3" stroke={GROEN} strokeWidth="3" strokeLinecap="round" />
          <g fill={DONKER}>
            <circle cx="10" cy="26" r="2.5" />
            <circle cx="15" cy="18" r="2" />
            <circle cx="56" cy="30" r="2" />
          </g>
        </svg>
      )
    case 'KERST':
      return (
        <svg {...basis}>
          <g fill={GROEN} stroke={DONKER} strokeWidth="2" strokeLinejoin="round">
            <path d="M32 30 12 52h40z" />
            <path d="M32 18 16 38h32z" />
            <path d="M32 8 20 26h24z" />
          </g>
          <rect x="28" y="52" width="8" height="8" fill={DONKER} />
          <path d="M32 1l1.8 3.8 4.2.6-3 3 .7 4.2L32 10.6l-3.7 2 .7-4.2-3-3 4.2-.6z" fill={DONKER} />
          <g fill={DONKER}>
            <circle cx="26" cy="35" r="2.5" />
            <circle cx="38" cy="46" r="2.5" />
            <circle cx="30" cy="24" r="2" />
          </g>
          <g fill={RAND}>
            <circle cx="8" cy="10" r="2" />
            <circle cx="56" cy="16" r="2.5" />
            <circle cx="60" cy="40" r="1.5" />
            <circle cx="6" cy="34" r="1.5" />
          </g>
        </svg>
      )
    case 'NIEUWJAAR':
      return (
        <svg {...basis}>
          <g stroke={DONKER} strokeWidth="2" strokeLinecap="round">
            <path d="M32 32V14M32 32l13-13M32 32h18M32 32l13 13M32 32v18M32 32 19 45M32 32H14M32 32 19 19" />
          </g>
          <g fill={GROEN} stroke={DONKER} strokeWidth="1.5">
            <circle cx="32" cy="12" r="3" />
            <circle cx="46" cy="18" r="3" />
            <circle cx="52" cy="32" r="3" />
            <circle cx="46" cy="46" r="3" />
            <circle cx="32" cy="52" r="3" />
            <circle cx="18" cy="46" r="3" />
            <circle cx="12" cy="32" r="3" />
            <circle cx="18" cy="18" r="3" />
          </g>
          <path d="M8 4l1.2 2.6 2.8.4-2 2 .5 2.8L8 10.4l-2.5 1.4.5-2.8-2-2 2.8-.4z" fill={GOUD} stroke={DONKER} strokeWidth="1" strokeLinejoin="round" />
          <path d="M56 50l1.2 2.6 2.8.4-2 2 .5 2.8-2.5-1.4-2.5 1.4.5-2.8-2-2 2.8-.4z" fill={GOUD} stroke={DONKER} strokeWidth="1" strokeLinejoin="round" />
        </svg>
      )
    case 'PASEN':
      return (
        <svg {...basis}>
          <path d="M22 56c-7 0-12-6-12-13 0-10 5-21 12-21s12 11 12 21c0 7-5 13-12 13z" fill={GROEN} stroke={DONKER} strokeWidth="2" />
          <path d="M12 40h20M13 47h18" stroke={DONKER} strokeWidth="2" strokeLinecap="round" />
          <path d="M44 56c-6 0-10-5-10-11 0-8 4-17 10-17s10 9 10 17c0 6-4 11-10 11z" fill={LICHT} stroke={DONKER} strokeWidth="2" />
          <g fill={DONKER}>
            <circle cx="41" cy="38" r="2" />
            <circle cx="48" cy="44" r="2" />
            <circle cx="42" cy="50" r="2" />
          </g>
          <path d="M2 60c5-6 9 0 13-6 4 6 8 0 13 6 4-6 8 0 12-6 4 6 8 0 13 6 4-6 8 0 10 0" stroke={GROEN} strokeWidth="3" strokeLinecap="round" />
        </svg>
      )
    case 'KONINGSDAG':
      return (
        <svg {...basis}>
          <path d="M10 24l10 12 12-18 12 18 10-12-4 30H14z" fill={ORANJE} stroke={DONKER} strokeWidth="2" strokeLinejoin="round" />
          <rect x="14" y="52" width="36" height="6" fill={DONKER} />
          <g fill={GROEN} stroke={DONKER} strokeWidth="1.5">
            <circle cx="32" cy="40" r="3.5" />
            <circle cx="10" cy="22" r="3" />
            <circle cx="32" cy="16" r="3" />
            <circle cx="54" cy="22" r="3" />
          </g>
          <rect x="48" y="2" width="14" height="3.5" fill={ORANJE} />
          <rect x="48" y="5.5" width="14" height="3.5" fill="#FFFFFF" stroke={RAND} strokeWidth="0.5" />
          <rect x="48" y="9" width="14" height="3.5" fill="#21468B" />
        </svg>
      )
  }
}

/**
 * Het logo-accent (muts, mijter, hoedje, sterretje, ei, kroontje), 24x24
 * viewBox. Wordt als kleine overlay op het STREEP-logo gezet; decoratief.
 */
export function ThemaLogoAccentMotief({ soort, className }: MotiefProps) {
  const basis = {
    viewBox: '0 0 24 24',
    fill: 'none',
    'aria-hidden': true as const,
    focusable: 'false' as const,
    className,
  }
  switch (soort) {
    case 'VERJAARDAG':
      return (
        <svg {...basis}>
          <path d="M12 3 5 21h14z" fill={GROEN} stroke={DONKER} strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M8.6 12h6.8M6.6 17h10.8" stroke={DONKER} strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="12" cy="3" r="2" fill={DONKER} />
        </svg>
      )
    case 'SINTERKLAAS':
      return (
        <svg {...basis}>
          <path d="M12 2c4 3 7 8 7 14H5c0-6 3-11 7-14z" fill={GROEN} stroke={DONKER} strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M12 7v7M9 10.5h6" stroke={DONKER} strokeWidth="1.5" strokeLinecap="round" />
          <rect x="4" y="16" width="16" height="4" rx="1" fill={DONKER} />
        </svg>
      )
    case 'KERST':
      return (
        <svg {...basis}>
          <path d="M5 17c1-8 5-13 12-13 1 2 1 5-1 8l-2 5z" fill={GROEN} stroke={DONKER} strokeWidth="1.5" strokeLinejoin="round" />
          <rect x="3" y="16" width="14" height="4" rx="2" fill={LICHT} stroke={DONKER} strokeWidth="1.5" />
          <circle cx="18.5" cy="4.5" r="2.5" fill={LICHT} stroke={DONKER} strokeWidth="1.5" />
        </svg>
      )
    case 'NIEUWJAAR':
      return (
        <svg {...basis}>
          <path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.2 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8z" fill={GOUD} stroke={DONKER} strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      )
    case 'PASEN':
      return (
        <svg {...basis}>
          <path d="M12 2c4 0 7 6 7 12 0 4.5-3 8-7 8s-7-3.5-7-8c0-6 3-12 7-12z" fill={GROEN} stroke={DONKER} strokeWidth="1.5" />
          <path d="M6 12h12M7 16h10" stroke={DONKER} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      )
    case 'KONINGSDAG':
      return (
        <svg {...basis}>
          <path d="M3 8l5 5 4-8 4 8 5-5-2 12H5z" fill={ORANJE} stroke={DONKER} strokeWidth="1.5" strokeLinejoin="round" />
          <circle cx="12" cy="14" r="1.6" fill={GROEN} />
        </svg>
      )
  }
}
